/**
 * Session store — the signed-in user, their workspaces, and what the deployment
 * has configured (auth methods, dev mode, AI provider).
 *
 * `bootstrap()` runs once on mount; every page reads from here rather than
 * calling /api/auth/me again.
 */

import { create } from 'zustand';
import type { AuthMethods, MeResponse } from '../../shared/api';
import type { AiProvider, Membership, ThemePreference, User, WorkspaceSummary } from '../../shared/types';
import { api, onUnauthorized } from '../lib/api';
import { clearAllQueries } from '../lib/query';

export type SessionStatus = 'idle' | 'loading' | 'authenticated' | 'anonymous';

interface SessionState {
  status: SessionStatus;
  user: User | null;
  memberships: Membership[];
  workspaces: WorkspaceSummary[];
  authMethods: AuthMethods;
  setupRequired: boolean;
  devMode: boolean;
  simulatePublish: boolean;
  aiProvider: AiProvider;
  theme: ThemePreference;
  error: string | null;

  bootstrap: () => Promise<void>;
  refresh: () => Promise<void>;
  applyMe: (me: MeResponse) => void;
  signOut: () => Promise<void>;
  setTheme: (theme: ThemePreference) => void;
}

const DEFAULT_AUTH_METHODS: AuthMethods = {
  google: false,
  magicLink: true,
  devLogin: false,
  emailDelivery: false,
};

const THEME_KEY = 'pap-theme';

function readStoredTheme(): ThemePreference {
  try {
    const value = localStorage.getItem(THEME_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') return value;
  } catch {
    /* private mode / storage disabled */
  }
  return 'dark';
}

/** Keep the <html class="dark"> flag in sync with the preference. */
export function applyTheme(theme: ThemePreference): void {
  const dark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* ignore */
  }
}

export const useSessionStore = create<SessionState>()((set, get) => ({
  status: 'idle',
  user: null,
  memberships: [],
  workspaces: [],
  authMethods: DEFAULT_AUTH_METHODS,
  setupRequired: false,
  devMode: false,
  simulatePublish: false,
  aiProvider: 'mock',
  theme: readStoredTheme(),
  error: null,

  applyMe: (me) =>
    set({
      status: me.user ? 'authenticated' : 'anonymous',
      user: me.user,
      memberships: me.memberships,
      workspaces: me.workspaces,
      authMethods: me.authMethods,
      setupRequired: me.setupRequired,
      devMode: me.devMode,
      simulatePublish: me.simulatePublish,
      aiProvider: me.aiProvider,
      error: null,
    }),

  bootstrap: async () => {
    if (get().status === 'loading') return;
    set({ status: 'loading' });
    try {
      const me = await api.auth.me();
      get().applyMe(me);
    } catch (error) {
      set({
        status: 'anonymous',
        user: null,
        error: error instanceof Error ? error.message : 'Could not reach the server.',
      });
    }
  },

  refresh: async () => {
    try {
      const me = await api.auth.me();
      get().applyMe(me);
    } catch {
      /* keep the previous state — a transient failure should not sign the user out */
    }
  },

  signOut: async () => {
    try {
      await api.auth.logout();
    } finally {
      clearAllQueries();
      set({
        status: 'anonymous',
        user: null,
        memberships: [],
        workspaces: [],
      });
    }
  },

  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
  },
}));

/** A 401 from any call drops us back to the signed-out state. */
onUnauthorized(() => {
  const state = useSessionStore.getState();
  if (state.status === 'authenticated') {
    clearAllQueries();
    useSessionStore.setState({ status: 'anonymous', user: null, memberships: [], workspaces: [] });
  }
});

/* Selectors ---------------------------------------------------------- */

export const selectIsAuthenticated = (state: SessionState) => state.status === 'authenticated';
export const selectIsSuperadmin = (state: SessionState) => Boolean(state.user?.isSuperadmin);
export const selectUsesMockAi = (state: SessionState) => state.aiProvider === 'mock';
